import React, { useEffect, useState } from 'react';
import { Book, BookNote } from '../objects/Book';
import { listNotes, createNote } from '../repository/readingRepository';

interface BookNotesPreviewProps {
  book: Book;
  onEdit: () => void;
}

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
};

const BookNotesPreview: React.FC<BookNotesPreviewProps> = ({ book, onEdit }) => {
  const [notes, setNotes] = useState<BookNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [isComposing, setIsComposing] = useState(false);
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchNotes = async () => {
      setLoading(true);
      const result = await listNotes(book.uuid);
      if (isMounted) {
        setNotes(result);
        setLoading(false);
      }
    };
    fetchNotes();
    return () => {
      isMounted = false;
    };
  }, [book.uuid]);

  const handleSaveNew = async () => {
    setIsSaving(true);
    try {
      const saved = await createNote(book.uuid, content);
      setNotes([saved, ...notes]);
      setContent('');
      setIsComposing(false);
    } catch {
      // fall through
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-section">
          <h3>Notes</h3>
          <p className="helper-text">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="card-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3>Notes ({notes.length})</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!isComposing && (
              <button className="button small secondary" onClick={() => setIsComposing(true)}>
                Add note
              </button>
            )}
            {notes.length > 0 && (
              <button className="button small" onClick={onEdit}>
                View notes
              </button>
            )}
          </div>
        </div>

        {isComposing && (
          <div style={{ marginBottom: '0.75rem' }}>
            <textarea
              className="textarea"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note..."
              autoFocus
            />
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              <button
                className="button small"
                onClick={handleSaveNew}
                disabled={isSaving || content.trim().length === 0}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button className="button small secondary" onClick={() => { setIsComposing(false); setContent(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {notes.length > 0 ? (
          notes.map((note) => (
            <div
              key={note.uuid}
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text)',
                background: '#f8fafc',
                padding: '0.75rem',
                borderRadius: 'var(--radius-md)',
                marginBottom: '0.5rem',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{note.content}</div>
              <div className="helper-text" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                {formatDate(note.created_at)}
                {note.updated_at && note.updated_at !== note.created_at && ` (edited ${formatDate(note.updated_at)})`}
              </div>
            </div>
          ))
        ) : (
          !isComposing && <p className="helper-text">No notes yet.</p>
        )}
      </div>
    </div>
  );
};

export default BookNotesPreview;
